"use client";

import { useEffect, useRef, type ReactNode } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useScrollTriggerRefresh } from "@/lib/use-scroll-trigger-refresh";
import { getLenisInstance, isScrolling } from "@/lib/lenis-instance";

if (typeof window !== "undefined") gsap.registerPlugin(ScrollTrigger);

export function CaseStudyReveal({ children }: { children: ReactNode }) {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>("[data-reveal-group]", root).forEach((group) => {
        const meta = gsap.utils.toArray<HTMLElement>("[data-meta]", group);
        if (meta.length) {
          gsap.fromTo(
            meta,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              stagger: 0.08,
              ease: "power3.out",
              scrollTrigger: { trigger: group, start: "top 80%", end: "top 45%", scrub: true },
            }
          );
        }

        const frame = group.querySelector<HTMLElement>("[data-frame]");
        const img = group.querySelector<HTMLElement>("[data-img]");
        if (frame) {
          gsap.fromTo(
            frame,
            { clipPath: "inset(0% 0% 100% 0%)" },
            {
              clipPath: "inset(0% 0% 0% 0%)",
              ease: "power2.out",
              scrollTrigger: { trigger: frame, start: "top 80%", end: "top 40%", scrub: true },
            }
          );
        }
        if (img) {
          gsap.fromTo(
            img,
            { opacity: 0 },
            {
              opacity: 1,
              ease: "power2.out",
              scrollTrigger: { trigger: frame ?? img, start: "top 80%", end: "top 40%", scrub: true },
            }
          );
        }
      });
    }, root);

    return () => ctx.revert();
  }, []);

  // Lenis caches its scrollable limit at init, before the cover image and in-content images load
  // and grow the page. Without re-measuring, wheel/trackpad scroll clamps short of the footer (a
  // scrollbar drag still reaches it — the tell). Re-measure after mount and on each image load,
  // debounced and deferred to scroll-idle so a burst of loads is one refresh and never fires
  // mid-gesture (which tugs scroll backwards).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    let timeout: ReturnType<typeof setTimeout>;
    const remeasure = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        // Resizing Lenis (updating its cached scroll limit) is safe mid-scroll and is exactly what
        // fixes wheel-scroll clamping short of the footer, so do it immediately. Only the
        // ScrollTrigger.refresh() must wait for scroll-idle to avoid tugging the position back.
        getLenisInstance()?.resize();
        (function refreshWhenIdle() {
          if (isScrolling()) {
            timeout = setTimeout(refreshWhenIdle, 150);
            return;
          }
          ScrollTrigger.refresh();
        })();
      }, 200);
    };
    const t = setTimeout(remeasure, 250);
    const imgs = Array.from(root.querySelectorAll("img"));
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", remeasure, { once: true });
    });
    return () => {
      clearTimeout(t);
      clearTimeout(timeout);
      imgs.forEach((img) => img.removeEventListener("load", remeasure));
    };
  }, []);

  useScrollTriggerRefresh([]);

  return <div ref={rootRef}>{children}</div>;
}
